// Mirrors apps/backend/internal/modules/documents/application/dto.go.

export type DocumentStatus = 'pending' | 'generated' | 'sent' | 'signed' | 'completed'

export type RegistrationStatus = 'preparing' | 'submitted' | 'registered' | 'completed'

export type RegistrationItemType =
  | 'boat_title'
  | 'boat_registration'
  | 'trailer_title'
  | 'trailer_registration'
  | 'sales_tax'
  | 'uscg_documentation'
  | 'lien_filing'
  | 'warranty'
  | 'other'

export type RegistrationItemStatus =
  | 'pending'
  | 'in_progress'
  | 'submitted'
  | 'completed'
  | 'not_applicable'

export interface DocumentTemplate {
  id: string
  key: string
  name: string
  category: string
  body: string
  required_stage: string
  is_system: boolean
  created_at: string
  updated_at: string
}

export interface DealDocument {
  id: string
  deal_id: string
  template_id?: string | null
  template_key: string
  template_name: string
  status: DocumentStatus
  has_rendered_body: boolean
  has_file: boolean
  file_content_type: string
  file_size: number
  notes: string
  generated_at?: string | null
  completed_at?: string | null
  created_at: string
  updated_at: string
}

export interface RegistrationItem {
  id: string
  registration_id: string
  item_type: RegistrationItemType
  label: string
  status: RegistrationItemStatus
  due_date?: string | null
  notes: string
  completed_at?: string | null
  created_at: string
  updated_at: string
}

export interface RegBoatBrief {
  id: string
  make: string
  model: string
  year: number
}

export interface RegCustomerBrief {
  id: string
  first_name: string
  last_name: string
}

export interface Registration {
  id: string
  deal_id: string
  boat_id?: string | null
  customer_id?: string | null
  state: string
  status: RegistrationStatus
  temp_tag_expires_at?: string | null
  lienholder_name: string
  notes: string
  completed_at?: string | null
  created_at: string
  updated_at: string
  items: RegistrationItem[]
  // Present on list reads only (work-queue view).
  deal_title: string
  boat?: RegBoatBrief | null
  customer?: RegCustomerBrief | null
}

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  pending: 'Pending',
  generated: 'Generated',
  sent: 'Sent',
  signed: 'Signed',
  completed: 'Completed',
}

export const DOCUMENT_STATUS_VARIANT: Record<DocumentStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  pending: 'outline',
  generated: 'secondary',
  sent: 'secondary',
  signed: 'default',
  completed: 'default',
}

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, string> = {
  preparing: 'Preparing',
  submitted: 'Submitted',
  registered: 'Registered',
  completed: 'Completed',
}

export const REGISTRATION_STATUS_VARIANT: Record<RegistrationStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  preparing: 'outline',
  submitted: 'secondary',
  registered: 'secondary',
  completed: 'default',
}

export const REGISTRATION_ITEM_TYPE_LABELS: Record<RegistrationItemType, string> = {
  boat_title: 'Boat title',
  boat_registration: 'Boat registration',
  trailer_title: 'Trailer title',
  trailer_registration: 'Trailer registration',
  sales_tax: 'Sales tax',
  uscg_documentation: 'USCG documentation',
  lien_filing: 'Lien filing',
  warranty: 'Warranty',
  other: 'Other',
}

export const REGISTRATION_ITEM_STATUS_LABELS: Record<RegistrationItemStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  completed: 'Completed',
  not_applicable: 'N/A',
}

export const ALL_ITEM_TYPES = Object.keys(REGISTRATION_ITEM_TYPE_LABELS) as RegistrationItemType[]
